import React, { useState, useMemo } from 'react';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import api from '../../../config/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PASSWORD_RULES = [
    { key: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
    { key: 'uppercase', label: 'One uppercase letter (A–Z)', test: (pw) => /[A-Z]/.test(pw) },
    { key: 'lowercase', label: 'One lowercase letter (a–z)', test: (pw) => /[a-z]/.test(pw) },
    { key: 'number', label: 'One number (0–9)', test: (pw) => /\d/.test(pw) },
    { key: 'special', label: 'One special character (!@#$...)', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

const getStrength = (passed) => {
    if (passed <= 1) return { level: 'weak', label: 'Weak', color: 'danger', percent: 20 };
    if (passed <= 2) return { level: 'fair', label: 'Fair', color: 'warning', percent: 40 };
    if (passed <= 3) return { level: 'good', label: 'Good', color: 'info', percent: 60 };
    if (passed <= 4) return { level: 'strong', label: 'Strong', color: 'primary', percent: 80 };
    return { level: 'excellent', label: 'Excellent', color: 'success', percent: 100 };
};

const ChangePassword = () => {
    const [form, setForm] = useState({
        old_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [loading, setLoading] = useState(false);
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const ruleResults = useMemo(
        () => PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(form.new_password) })),
        [form.new_password]
    );
    const passedCount = ruleResults.filter((r) => r.passed).length;
    const strength = getStrength(passedCount);
    const passwordsMatch =
        form.confirm_password.length > 0 && form.new_password === form.confirm_password;
    const passwordsMismatch =
        form.confirm_password.length > 0 && form.new_password !== form.confirm_password;

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.old_password || !form.new_password || !form.confirm_password) {
            toast.error('All fields are required.');
            return;
        }
        if (form.new_password !== form.confirm_password) {
            toast.error('New password and confirm password do not match.');
            return;
        }
        if (passedCount < PASSWORD_RULES.length) {
            toast.error('New password does not meet all requirements.');
            return;
        }
        setLoading(true);
        try {
            await api.post('/api/change-password/', {
                old_password: form.old_password,
                new_password: form.new_password,
                confirm_password: form.confirm_password,
            });
            toast.success('Password changed successfully.');
            setForm({ old_password: '', new_password: '', confirm_password: '' });
        } catch (err) {
            toast.error(
                err.response?.data?.detail ||
                    err.response?.data?.message ||
                    'Failed to change password.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-sidebar-skin="dark"
            data-header-skin="light"
            data-navbar-brand-skin="dark"
            data-sidebar-state="expand"
        >
            <style>{`
                .cp-strength-bar {
                    height: 6px;
                    border-radius: 3px;
                    background-color: #e9ecef;
                    overflow: hidden;
                }
                .cp-strength-fill {
                    transition: width 0.4s ease, background-color 0.4s ease;
                    border-radius: 3px;
                }
                .cp-strength-section {
                    animation: cp-fade-in 0.25s ease;
                }
                .cp-requirement-item {
                    transition: background-color 0.25s ease, transform 0.2s ease;
                    border: 1px solid transparent;
                }
                .cp-req-neutral {
                    background-color: #f8f9fa;
                    border-color: #e9ecef;
                }
                .cp-req-passed {
                    background-color: #d1fae5;
                    border-color: #a7f3d0;
                }
                .cp-req-failed {
                    background-color: #fee2e2;
                    border-color: #fecaca;
                }
                .cp-req-icon {
                    transition: transform 0.3s ease;
                    flex-shrink: 0;
                }
                .cp-req-passed .cp-req-icon {
                    animation: cp-check-pop 0.35s ease;
                }
                .cp-tips-list li::marker {
                    color: #9ca3af;
                }
                @keyframes cp-fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(-4px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes cp-check-pop {
                    0% { transform: scale(0.5); }
                    60% { transform: scale(1.25); }
                    100% { transform: scale(1); }
                }
            `}</style>
            <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">
                    {/* Page Header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Change Password</h4>
                            <p className="text-muted mb-0 fs-13">
                                Update your account security password
                            </p>
                        </div>
                    </div>

                    <div className="row g-4 mb-4">
                        {/* Form Card */}
                        <div className="col-12 col-lg-7">
                            <div className="card shadow-sm border-0">
                                <div className="card-header bg-light border-bottom py-3">
                                    <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                        <i className="ti ti-shield-lock fs-20 text-primary" />
                                        Password Management
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        {/* Old Password */}
                                        <div className="mb-3">
                                            <label htmlFor="old_password" className="form-label fw-medium">
                                                Old Password <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light text-muted">
                                                    <i className="ti ti-key" />
                                                </span>
                                                <input
                                                    type={showOld ? 'text' : 'password'}
                                                    id="old_password"
                                                    name="old_password"
                                                    value={form.old_password}
                                                    onChange={handleChange}
                                                    className="form-control"
                                                    placeholder="Enter your current password"
                                                    autoComplete="current-password"
                                                    disabled={loading}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setShowOld((v) => !v)}
                                                    tabIndex={-1}
                                                    disabled={loading}
                                                >
                                                    <i className={`ti ti-${showOld ? 'eye-off' : 'eye'}`} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* New Password */}
                                        <div className="mb-3">
                                            <label htmlFor="new_password" className="form-label fw-medium">
                                                New Password <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light text-muted">
                                                    <i className="ti ti-lock" />
                                                </span>
                                                <input
                                                    type={showNew ? 'text' : 'password'}
                                                    id="new_password"
                                                    name="new_password"
                                                    value={form.new_password}
                                                    onChange={handleChange}
                                                    className="form-control"
                                                    placeholder="Enter your new password"
                                                    autoComplete="new-password"
                                                    disabled={loading}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setShowNew((v) => !v)}
                                                    tabIndex={-1}
                                                    disabled={loading}
                                                >
                                                    <i className={`ti ti-${showNew ? 'eye-off' : 'eye'}`} />
                                                </button>
                                            </div>

                                            {/* Strength Bar */}
                                            {form.new_password.length > 0 && (
                                                <div className="cp-strength-section mt-2">
                                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                                        <span className="fs-12 text-muted">Password strength</span>
                                                        <span className={`badge bg-${strength.color}-subtle text-${strength.color} border border-${strength.color}-subtle fs-11`}>
                                                            {strength.label}
                                                        </span>
                                                    </div>
                                                    <div className="progress cp-strength-bar">
                                                        <div
                                                            className={`progress-bar bg-${strength.color} cp-strength-fill`}
                                                            role="progressbar"
                                                            style={{ width: `${strength.percent}%` }}
                                                            aria-valuenow={strength.percent}
                                                            aria-valuemin="0"
                                                            aria-valuemax="100"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Confirm New Password */}
                                        <div className="mb-4">
                                            <label htmlFor="confirm_password" className="form-label fw-medium">
                                                Confirm New Password <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light text-muted">
                                                    <i className="ti ti-lock-check" />
                                                </span>
                                                <input
                                                    type={showConfirm ? 'text' : 'password'}
                                                    id="confirm_password"
                                                    name="confirm_password"
                                                    value={form.confirm_password}
                                                    onChange={handleChange}
                                                    className={`form-control ${passwordsMatch ? 'is-valid' : ''} ${passwordsMismatch ? 'is-invalid' : ''}`}
                                                    placeholder="Re-enter your new password"
                                                    autoComplete="new-password"
                                                    disabled={loading}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setShowConfirm((v) => !v)}
                                                    tabIndex={-1}
                                                    disabled={loading}
                                                >
                                                    <i className={`ti ti-${showConfirm ? 'eye-off' : 'eye'}`} />
                                                </button>
                                            </div>
                                            {passwordsMismatch && (
                                                <div className="invalid-feedback d-block fs-12 mt-1">
                                                    <i className="ti ti-alert-circle me-1" />
                                                    Passwords do not match
                                                </div>
                                            )}
                                            {passwordsMatch && (
                                                <div className="valid-feedback d-block fs-12 mt-1">
                                                    <i className="ti ti-circle-check me-1" />
                                                    Passwords match
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                                            disabled={loading || passwordsMismatch || (form.new_password.length > 0 && passedCount < PASSWORD_RULES.length)}
                                        >
                                            {loading ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    <span>Changing...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ti ti-check fs-18" />
                                                    <span>Change Password</span>
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Requirements Sidebar Card */}
                        <div className="col-12 col-lg-5">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-header bg-light border-bottom py-3">
                                    <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                        <i className="ti ti-list-check fs-20 text-primary" />
                                        Password Requirements
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted fs-13 mb-3">
                                        Your new password must meet all of the following criteria:
                                    </p>
                                    <ul className="list-unstyled mb-0 cp-requirements-list">
                                        {ruleResults.map((rule) => (
                                            <li
                                                key={rule.key}
                                                className={`cp-requirement-item d-flex align-items-center gap-2 py-2 px-3 rounded-2 mb-2 ${
                                                    form.new_password.length === 0
                                                        ? 'cp-req-neutral'
                                                        : rule.passed
                                                        ? 'cp-req-passed'
                                                        : 'cp-req-failed'
                                                }`}
                                            >
                                                <i
                                                    className={`ti ${
                                                        form.new_password.length === 0
                                                            ? 'ti-circle text-muted'
                                                            : rule.passed
                                                            ? 'ti-circle-check-filled text-success'
                                                            : 'ti-circle-x-filled text-danger'
                                                    } fs-18 cp-req-icon`}
                                                />
                                                <span className="fs-13">{rule.label}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Security Tips */}
                                    <div className="mt-4 pt-3 border-top">
                                        <h6 className="fw-bold text-dark fs-13 mb-2 d-flex align-items-center gap-2">
                                            <i className="ti ti-bulb text-warning" />
                                            Security Tips
                                        </h6>
                                        <ul className="mb-0 ps-3 cp-tips-list">
                                            <li className="text-muted fs-12 mb-1">
                                                Never share your password with anyone
                                            </li>
                                            <li className="text-muted fs-12 mb-1">
                                                Avoid using personal information
                                            </li>
                                            <li className="text-muted fs-12 mb-1">
                                                Use a unique password for each account
                                            </li>
                                            <li className="text-muted fs-12">
                                                Consider using a password manager
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        2026 © <span className="link-primary">Casoe Technologies</span>, All Rights
                        Reserved
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;